// The firm's details (onboarding while pending), password change and the firm's users.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
  toast,
} from '@tatra/ui';
import { type ReactNode, useState } from 'react';
import { FormError, Loading } from '../components.js';
import { ApiError, errorMessage, get, patch, post, type Me, type TeamUser } from '../lib/api.js';
import { dateOnly } from '../lib/format.js';
import { useSession } from '../lib/session.js';

export function ProfilePage() {
  const { me, isOwner, approved, reloadMe } = useSession();
  if (!me) return <Loading />;
  const editable = isOwner && (me.status === 'PENDING' || me.status === 'REJECTED');
  return (
    <>
      <PageHeader
        title={approved ? 'Your profile' : 'Your details'}
        description={
          editable
            ? 'We review these before you can register customers. Your PAN and bank number are stored encrypted and never shown again.'
            : me.name
        }
      />
      <div className="space-y-6">
        {me.status === 'REJECTED' && me.decisionNote && (
          <p role="status" className="rounded-md border border-danger/40 bg-danger-bg p-3 text-sm text-text">
            <span className="font-medium">Why it was not approved: </span>
            {me.decisionNote}
          </p>
        )}
        {editable && me.missing.length > 0 && (
          <Card className="p-4">
            <h2 className="mb-1 text-sm font-semibold text-text">Still needed</h2>
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-text-muted">
              {me.missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Card>
        )}
        {editable ? (
          <FullForm me={me} onSaved={reloadMe} />
        ) : isOwner ? (
          <ContactForm me={me} onSaved={reloadMe} />
        ) : (
          <ReadOnly me={me} />
        )}
        {!editable && <OnFile me={me} />}
        <PasswordForm />
      </div>
    </>
  );
}

const clean = (o: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(o)
      .filter(([, v]) => v.trim() !== '')
      .map(([k, v]) => [k, v.trim()]),
  );

function FullForm({ me, onSaved }: { me: Me; onSaved: () => void }) {
  const [f, setF] = useState({
    name: me.name,
    kind: me.kind,
    contactName: me.contactName,
    phone: me.phone ?? '',
    city: me.city ?? '',
    address: me.address ?? '',
    pan: '',
    gstin: '',
    bankAccountName: me.bankAccountName ?? '',
    bankAccountNumber: '',
    bankIfsc: me.bankIfsc ?? '',
    bankName: me.bankName ?? '',
    reraNumber: me.reraNumber ?? '',
    reraState: me.reraState ?? '',
    reraValidUntil: me.reraValidUntil ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => patch<Me>('/me/profile', clean(f)),
    onSuccess: () => {
      toast.success(me.status === 'REJECTED' ? 'Sent for review again' : 'Details saved');
      onSaved();
    },
  });
  const fe = (k: string) => (save.error instanceof ApiError ? save.error.fieldError(k) : undefined);
  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Section title="Your firm">
        <Field label="Firm or your name" required error={fe('name')}>
          {(p) => <Input {...p} value={f.name} onChange={set('name')} required />}
        </Field>
        <Field label="Type of business">
          {(p) => (
            <Select {...p} value={f.kind} onChange={set('kind')}>
              <option value="INDIVIDUAL">Individual agent</option>
              <option value="PROPRIETORSHIP">Proprietorship</option>
              <option value="PARTNERSHIP">Partnership</option>
              <option value="COMPANY">Company</option>
            </Select>
          )}
        </Field>
        <Field label="Contact person" required error={fe('contactName')}>
          {(p) => <Input {...p} value={f.contactName} onChange={set('contactName')} required />}
        </Field>
        <Field label="Phone" error={fe('phone')}>
          {(p) => <Input {...p} type="tel" inputMode="tel" value={f.phone} onChange={set('phone')} />}
        </Field>
        <Field label="City">{(p) => <Input {...p} value={f.city} onChange={set('city')} />}</Field>
        <Field label="Address" className="sm:col-span-2">
          {(p) => <Textarea {...p} rows={2} value={f.address} onChange={set('address')} />}
        </Field>
      </Section>
      <Section title="Identity and tax">
        <Field
          label="PAN"
          error={fe('pan')}
          help={me.kyc.pan ? `On file: ${me.kyc.pan}. Leave blank to keep it.` : 'For example ABCDE1234F'}
        >
          {(p) => (
            <Input
              {...p}
              value={f.pan}
              onChange={set('pan')}
              maxLength={10}
              autoCapitalize="characters"
              autoComplete="off"
            />
          )}
        </Field>
        <Field
          label="GSTIN (if registered)"
          error={fe('gstin')}
          help={me.kyc.gstin ? `On file: ${me.kyc.gstin}. Leave blank to keep it.` : undefined}
        >
          {(p) => (
            <Input
              {...p}
              value={f.gstin}
              onChange={set('gstin')}
              maxLength={15}
              autoCapitalize="characters"
              autoComplete="off"
            />
          )}
        </Field>
      </Section>
      <Section title="Bank account for payouts">
        <Field label="Account holder name">
          {(p) => <Input {...p} value={f.bankAccountName} onChange={set('bankAccountName')} />}
        </Field>
        <Field
          label="Account number"
          error={fe('bankAccountNumber')}
          help={me.kyc.bankAccount ? `On file: ${me.kyc.bankAccount}. Leave blank to keep it.` : undefined}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              value={f.bankAccountNumber}
              onChange={set('bankAccountNumber')}
              maxLength={18}
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="IFSC" error={fe('bankIfsc')} help="For example HDFC0001234">
          {(p) => (
            <Input
              {...p}
              value={f.bankIfsc}
              onChange={set('bankIfsc')}
              maxLength={11}
              autoCapitalize="characters"
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="Bank name">
          {(p) => <Input {...p} value={f.bankName} onChange={set('bankName')} />}
        </Field>
      </Section>
      <Section title="RERA registration">
        <Field label="RERA agent number" error={fe('reraNumber')}>
          {(p) => <Input {...p} value={f.reraNumber} onChange={set('reraNumber')} />}
        </Field>
        <Field label="State">{(p) => <Input {...p} value={f.reraState} onChange={set('reraState')} />}</Field>
        <Field label="Valid until" error={fe('reraValidUntil')}>
          {(p) => <Input {...p} type="date" value={f.reraValidUntil} onChange={set('reraValidUntil')} />}
        </Field>
      </Section>
      <FormError message={save.isError ? errorMessage(save.error) : null} />
      <Button type="submit" loading={save.isPending}>
        {me.status === 'REJECTED' ? 'Save and send for review again' : 'Save details'}
      </Button>
    </form>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 font-medium text-text">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

function ContactForm({ me, onSaved }: { me: Me; onSaved: () => void }) {
  const [f, setF] = useState({
    contactName: me.contactName,
    phone: me.phone ?? '',
    city: me.city ?? '',
    address: me.address ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => patch<Me>('/me/profile', clean(f)),
    onSuccess: () => {
      toast.success('Contact details saved');
      onSaved();
    },
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Section title="Contact details">
        <Field label="Contact person" required>
          {(p) => <Input {...p} value={f.contactName} onChange={set('contactName')} required />}
        </Field>
        <Field label="Phone">
          {(p) => <Input {...p} type="tel" inputMode="tel" value={f.phone} onChange={set('phone')} />}
        </Field>
        <Field label="City">{(p) => <Input {...p} value={f.city} onChange={set('city')} />}</Field>
        <Field label="Address">
          {(p) => <Textarea {...p} rows={2} value={f.address} onChange={set('address')} />}
        </Field>
        <div className="sm:col-span-2">
          <FormError message={save.isError ? errorMessage(save.error) : null} />
          <Button type="submit" loading={save.isPending} className="mt-2">
            Save
          </Button>
        </div>
      </Section>
    </form>
  );
}

function ReadOnly({ me }: { me: Me }) {
  return (
    <Card className="p-4 text-sm">
      <h2 className="mb-2 font-medium text-text">Your account</h2>
      <p className="text-text-muted">
        {me.me.name} · {me.me.email} · agent of {me.name}
      </p>
    </Card>
  );
}

/** What stays with the developer's team after approval: shown, not editable. */
function OnFile({ me }: { me: Me }) {
  const rows: [string, string | null][] = [
    ['PAN', me.kyc.pan],
    ['GSTIN', me.kyc.gstin],
    ['Bank account', me.kyc.bankAccount],
    ['IFSC', me.bankIfsc],
    ['RERA agent number', me.reraNumber],
    ['RERA valid until', me.reraValidUntil ? dateOnly(me.reraValidUntil) : null],
  ];
  return (
    <Card className="p-4">
      <h2 className="mb-1 font-medium text-text">On file with us</h2>
      <p className="mb-3 text-sm text-text-muted">
        To change identity, bank or RERA details, ask your relationship manager.
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-text-muted">{k}</dt>
            <dd className="text-text">{v ?? '–'}</dd>
          </div>
        ))}
      </dl>
      {me.projects.length > 0 && (
        <p className="mt-3 text-sm text-text-muted">
          Your projects: {me.projects.map((p) => p.name).join(', ')}
        </p>
      )}
    </Card>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => post('/auth/password/change', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.success('Password changed');
      setCurrent('');
      setNext('');
    },
  });
  return (
    <Card className="p-4">
      <h2 className="mb-3 font-medium text-text">Change your password</h2>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
      >
        <Field label="Current password" required>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="New password" required>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          )}
        </Field>
        <div className="sm:col-span-2">
          <FormError message={change.isError ? errorMessage(change.error) : null} />
          <Button type="submit" loading={change.isPending} className="mt-2">
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function TeamPage() {
  const { me, approved } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['team'], queryFn: () => get<TeamUser[]>('/users') });
  const [f, setF] = useState({ name: '', email: '', phone: '', role: 'AGENT' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const add = useMutation({
    mutationFn: () => post<TeamUser>('/users', { ...f, phone: f.phone.trim() || null }),
    onSuccess: (u) => {
      toast.success(`Invitation sent to ${u.email}`);
      setF({ name: '', email: '', phone: '', role: 'AGENT' });
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
  const toggle = useMutation({
    mutationFn: (u: TeamUser) => post(`/users/${u.id}/${u.status === 'DISABLED' ? 'enable' : 'disable'}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <>
      <PageHeader
        title="Your team"
        description="Agents register their own customers and see only those. Owners see everything."
      />
      <div className="space-y-6">
        {q.isLoading ? (
          <Loading />
        ) : q.isError || !q.data ? (
          <ErrorState onRetry={() => void q.refetch()} />
        ) : q.data.length === 0 ? (
          <EmptyState title="Nobody yet" />
        ) : (
          <ul className="space-y-2">
            {q.data.map((u) => (
              <li key={u.id}>
                <Card className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text">
                      {u.name} <Badge>{u.role === 'OWNER' ? 'Owner' : 'Agent'}</Badge>
                    </p>
                    <p className="break-all text-sm text-text-muted">{u.email}</p>
                    <p className="text-xs text-text-faint">
                      {u.status === 'INVITED'
                        ? 'Invitation sent'
                        : u.status === 'DISABLED'
                          ? 'Switched off'
                          : u.lastLoginAt
                            ? `Last seen ${dateOnly(u.lastLoginAt)}`
                            : 'Active'}
                    </p>
                  </div>
                  {u.id !== me?.me.id && (
                    <Button
                      variant={u.status === 'DISABLED' ? 'secondary' : 'danger-ghost'}
                      loading={toggle.isPending && toggle.variables?.id === u.id}
                      onClick={() => toggle.mutate(u)}
                    >
                      {u.status === 'DISABLED' ? 'Switch on' : 'Switch off'}
                    </Button>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
        {approved ? (
          <Card className="p-4">
            <h2 className="mb-3 font-medium text-text">Invite someone</h2>
            <form
              className="grid gap-4 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                add.mutate();
              }}
            >
              <Field label="Name" required>
                {(p) => <Input {...p} value={f.name} onChange={set('name')} minLength={2} required />}
              </Field>
              <Field label="Email" required>
                {(p) => <Input {...p} type="email" value={f.email} onChange={set('email')} required />}
              </Field>
              <Field label="Phone">
                {(p) => <Input {...p} type="tel" inputMode="tel" value={f.phone} onChange={set('phone')} />}
              </Field>
              <Field label="Role">
                {(p) => (
                  <Select {...p} value={f.role} onChange={set('role')}>
                    <option value="AGENT">Agent: sees only their own customers</option>
                    <option value="OWNER">Owner: sees everything, claims commission</option>
                  </Select>
                )}
              </Field>
              <div className="sm:col-span-2">
                <FormError message={add.isError ? errorMessage(add.error) : null} />
                <Button type="submit" loading={add.isPending} className="mt-2">
                  Send invitation
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <p className="text-sm text-text-muted">You can invite people once your firm is approved.</p>
        )}
      </div>
    </>
  );
}
